import { useCallback, useEffect, useRef, useState } from 'react';
import type { HubHistoryEvent } from '@aiai/shared';
import { fetchHubHistory } from '../api';
import { AWAY_MS, readLastSeen, replaySummary, writeLastSeen } from './replay';

export interface ReplayOffer {
  summary: ReturnType<typeof replaySummary>;
  events: HubHistoryEvent[];
  since: string;
  until: string;
}

const TOUCH_MS = 60_000;
/** The away replay plays at most this many events (the history API returns up to 2000, oldest first). */
export const OFFER_MAX_EVENTS = 400;

/**
 * "While you were away": on mount reads the previous visit time (before stamping the new one),
 * keeps the stamp fresh while the page is visible, and, when the user was away long enough,
 * fetches what happened and exposes an offer to replay it. Failures and blocked storage show nothing.
 */
export function useReplay(): { offer: ReplayOffer | null; dismiss: () => void } {
  const [offer, setOffer] = useState<ReplayOffer | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // StrictMode re-runs effects: the second run would read the stamp the first just wrote.
    if (started.current) return;
    started.current = true;
    const prev = readLastSeen();
    const now = Date.now();
    writeLastSeen(new Date(now).toISOString());
    if (prev === null) return;
    const last = Date.parse(prev);
    if (!Number.isFinite(last) || now - last < AWAY_MS) return;
    fetchHubHistory(prev)
      .then((history) => {
        if (history.events.length === 0) return;
        // The away replay is 20 s: keep its density as it was by playing only the newest events, and
        // start the window at the first kept one so the cut never looks like a quiet stretch.
        const cut = history.events.length > OFFER_MAX_EVENTS;
        const events = cut ? history.events.slice(-OFFER_MAX_EVENTS) : history.events;
        const since = cut ? events[0].at : history.since;
        setOffer({ summary: replaySummary(events), events, since, until: history.until });
      })
      .catch(() => {
        // A history failure shows nothing.
      });
  }, []);

  useEffect(() => {
    const touch = () => writeLastSeen(new Date().toISOString());
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') touch();
    }, TOUCH_MS);
    window.addEventListener('pagehide', touch);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('pagehide', touch);
    };
  }, []);

  const dismiss = useCallback(() => setOffer(null), []);
  return { offer, dismiss };
}
